/* ==================== Public API ==================== */
function initStickyTOC( options ) {
  return new StickyTOCController( options );
}

/* ==================== Controller ==================== */
class StickyTOCController {
  constructor( {
    navSelector,
    headerSelector,
    headingSelector = 'h2',
    linkSelector = 'a',
    largeScreenWidth = 992,
    defaultOffset = 62, // sticky padding
    triggerOffsetDesktop = 120, // heading trigger distance for desktop
    triggerOffsetMobile = 80, // heading trigger distance for mobile
    marginTop = 12,
    marginBottom = 12,
    innerScrollDuration = 280,
    innerScrollEasing = ( t ) => 1 - ( 1 - t ) ** 3, // easeOutCubic
    visibilityBuffer = 12,
    epsilon = 1,
    scrollDuration = 500,
    edgeThreshold = 6 // px range to “snap/lock” at edges
  } ) {
    this.cfg = {
      navSelector, headerSelector, headingSelector, linkSelector,
      largeScreenWidth, defaultOffset,
      triggerOffsetDesktop, triggerOffsetMobile,
      marginTop, marginBottom,
      innerScrollDuration, innerScrollEasing,
      visibilityBuffer, epsilon, scrollDuration, edgeThreshold
    };

    // DOM
    this.toc = document.querySelector( navSelector );
    this.header = document.querySelector( headerSelector );
    if ( !this.toc || !this.header ) return;

    // Links + headings
    this.links = this.queryLinks();
    this.headings = this.queryHeadings( this.links );
    if ( !this.links.length || !this.headings.length ) return;

    // Auto-detect the section root that contains the linked headings
    this.section = this.detectSectionRoot();

    // State
    this.ticking = false;
    this._scrollAnim = null;
    this._animating = false;
    this._lastActiveEl = null;
    this._lastAim = null;
    this.allowScrollUpdate = true;
    this.edgeLock = null; // 'top' | 'bottom' | null

    // Bind
    this.onResize = this.onResize.bind( this );
    this.onScroll = this.onScroll.bind( this );
    this.onLinkClick = this.onLinkClick.bind( this );

    // Init
    this.init();
  }

  /* ==================== Setup ==================== */
  init() {
    window.addEventListener( 'resize', this.onResize );
    window.addEventListener( 'scroll', this.onScroll, { passive: true } );
    this.links.forEach( l => l.addEventListener( 'click', this.onLinkClick ) );

    // NEW: ensure first is active on load
    this.setActiveLink( 0 );

    this.onResize();
    this.onScroll();
  }

  destroy() {
    window.removeEventListener( 'resize', this.onResize );
    window.removeEventListener( 'scroll', this.onScroll );
    this.links.forEach( l => l.removeEventListener( 'click', this.onLinkClick ) );
    this.cancelInnerScrollAnim();
  }

  /* ==================== Queries ==================== */
  queryLinks() {
    const combined = this.cfg.linkSelector
      .split( ',' )
      .map( s => `${s.trim()}[href^="#"]` )
      .join( ', ' );
    return this.toc.querySelectorAll( combined );
  }

  queryHeadings( links ) {
    const ids = [...links].map( l => l.getAttribute( 'href' )?.slice( 1 ) ).filter( Boolean );
    return Array.from( document.querySelectorAll( this.cfg.headingSelector ) )
      .filter( h => h.id && ids.includes( h.id ) );
  }

  /* ==================== Section auto-detect ==================== */
  detectSectionRoot() {
    const allHeadings = this.headings;
    // nearest ancestor that contains *all* headings
    let node = this.toc;
    while ( node && node !== document.body ) {
      node = node.parentElement;
      if ( !node ) break;
      if ( allHeadings.every( h => node.contains( h ) ) ) return node;
    }
    // fallback: nearest <section> that contains *any* heading
    node = this.toc;
    while ( node && node !== document.body ) {
      node = node.parentElement;
      if ( !node ) break;
      if ( node.tagName && node.tagName.toLowerCase() === 'section' && allHeadings.some( h => node.contains( h ) ) ) {
        return node;
      }
    }
    // fallback: nearest parent
    return this.toc.parentElement || document.body;
  }

  /* ==================== Utilities ==================== */
  isDesktop() { return window.innerWidth >= this.cfg.largeScreenWidth; }
  headerHeight() { return this.header?.offsetHeight || 0; }
  prefersReducedMotion() { return window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches; }

  sectionTop() {
    if ( !this.section ) return null;
    const r = this.section.getBoundingClientRect();
    return r.top + window.scrollY;
  }
  sectionBottom() {
    if ( !this.section ) return null;
    const r = this.section.getBoundingClientRect();
    return r.top + window.scrollY + this.section.offsetHeight;
  }
  isSectionNearTop() {
    if ( !this.section ) return false;
    const st = this.sectionTop();
    if ( st == null ) return false;
    return window.scrollY <= ( st + this.cfg.edgeThreshold );
  }
  isSectionNearBottom() {
    if ( !this.section ) return false;
    const sb = this.sectionBottom();
    if ( sb == null ) return false;
    const viewportBottom = window.scrollY + window.innerHeight;
    return viewportBottom >= ( sb - this.cfg.edgeThreshold );
  }

  /* ==================== Layout (sticky + inner scroll) ==================== */
  applyDesktopStickyAndInnerScroll() {
    const stickyTop = this.headerHeight() + this.cfg.defaultOffset;
    Object.assign( this.toc.style, {
      position: 'sticky',
      top: `${stickyTop}px`,
      transition: 'top 0.2s ease'
    } );

    // Max height = screenHeight - (headerHeight + (defaultOffset * 2))
    const maxH = Math.max(
      120,
      window.innerHeight - ( this.headerHeight() + ( this.cfg.defaultOffset * 2 ) )
    );

    Object.assign( this.toc.style, {
      overflowY: 'auto',
      maxHeight: `${Math.round( maxH )}px`
    } );
  }

  clearMobileStyles() {
    this.cancelInnerScrollAnim();
    Object.assign( this.toc.style, {
      position: '',
      top: '',
      transition: '',
      overflowY: '',
      maxHeight: ''
    } );
  }

  /* ==================== Smooth inner scroll (for TOC box) ==================== */
  cancelInnerScrollAnim() {
    if ( this._scrollAnim?.raf ) cancelAnimationFrame( this._scrollAnim.raf );
    this._scrollAnim = null;
    this._animating = false;
  }

  smoothScrollContainerTo( targetScrollTop ) {
    const container = this.toc;
    const maxScroll = Math.max( 0, container.scrollHeight - container.clientHeight );
    const to = Math.min( maxScroll, Math.max( 0, Math.round( targetScrollTop ) ) );
    const from = container.scrollTop;
    const delta = to - from;

    // no-op if same target
    if ( Math.abs( delta ) < this.cfg.epsilon ) {
      container.scrollTop = to;
      this._lastAim = to;
      return;
    }
    if ( this._lastAim != null && Math.abs( to - this._lastAim ) < this.cfg.epsilon ) {
      return;
    }

    if ( this.prefersReducedMotion() || this.cfg.innerScrollDuration <= 0 ) {
      container.scrollTop = to;
      this._lastAim = to;
      return;
    }

    this.cancelInnerScrollAnim();
    this._animating = true;
    this._lastAim = to;

    const startTime = performance.now();
    const duration = this.cfg.innerScrollDuration;
    const ease = this.cfg.innerScrollEasing;

    const step = ( now ) => {
      const t = Math.min( 1, ( now - startTime ) / duration );
      container.scrollTop = from + delta * ease( t );

      if ( t < 1 ) {
        this._scrollAnim = { raf: requestAnimationFrame( step ) };
      } else {
        this._animating = false;
        this._scrollAnim = null;
      }
    };

    this._scrollAnim = { raf: requestAnimationFrame( step ) };
  }

  /* ==================== Active link (scroll + click) ==================== */
  computeActiveIndex() {
    const triggerOffset = this.isDesktop()
      ? this.cfg.triggerOffsetDesktop
      : this.cfg.triggerOffsetMobile;

    let idx = -1;
    for ( let i = 0; i < this.headings.length; i++ ) {
      const top = this.headings[i].getBoundingClientRect().top;
      if ( top - triggerOffset <= 0 ) idx = i;
      else break;
    }
    return idx;
  }

  setActiveLink( index ) {
    this.links.forEach( l => l.classList.remove( 'active' ) );
    if ( index >= 0 && index < this.links.length ) {
      this.links[index].classList.add( 'active' );
    }
  }

  /* ==================== Keep active visible (inside TOC scroller) ==================== */
  ensureActiveVisible( { smooth = true } = {} ) {
    if ( !this.isDesktop() ) return;

    // If we're locked to an edge, skip centering to avoid tug-of-war
    if ( this.edgeLock === 'top' || this.edgeLock === 'bottom' ) return;

    const selectorList = this.cfg.linkSelector
      .split( ',' )
      .map( s => s.trim() )
      .join( ', ' );
    const active = this.toc.querySelector( `${selectorList}.active` );
    if ( !active ) return;

    if ( active !== this._lastActiveEl ) {
      this.cancelInnerScrollAnim();
      this._lastActiveEl = active;
      this._lastAim = null;
    }

    const container = this.toc;
    const containerRect = container.getBoundingClientRect();
    const linkRect = active.getBoundingClientRect();
    const curScroll = container.scrollTop;

    const safeTop = curScroll + this.cfg.marginTop + this.cfg.visibilityBuffer;
    const safeBottom = curScroll + container.clientHeight - this.cfg.marginBottom - this.cfg.visibilityBuffer;

    const linkTop = ( linkRect.top - containerRect.top ) + curScroll;
    const linkBottom = ( linkRect.bottom - containerRect.top ) + curScroll;

    let target = null;
    if ( linkTop < safeTop ) {
      target = linkTop - ( this.cfg.marginTop + this.cfg.visibilityBuffer );
    } else if ( linkBottom > safeBottom ) {
      target = linkBottom - ( container.clientHeight - this.cfg.marginBottom - this.cfg.visibilityBuffer );
    }

    if ( target == null ) return;
    if ( this._animating && this._lastAim != null && Math.abs( target - this._lastAim ) < this.cfg.epsilon ) return;
    if ( Math.abs( target - curScroll ) < this.cfg.epsilon ) return;

    if ( smooth ) this.smoothScrollContainerTo( target );
    else {
      container.scrollTop = Math.round( target );
      this._lastAim = target;
    }
  }

  /* ==================== Sync TOC when SECTION hits ends (with edge locks) ==================== */
  scrollTOCToEnd( { smooth = true } = {} ) {
    if ( !this.isDesktop() ) return;
    const container = this.toc;
    const maxScroll = Math.max( 0, container.scrollHeight - container.clientHeight );
    const curScroll = container.scrollTop;
    if ( Math.abs( maxScroll - curScroll ) < this.cfg.epsilon ) return; // already at end
    this.edgeLock = 'bottom';
    this.setActiveLink( this.links.length - 1 ); // NEW: ensure last link is active at bottom
    if ( smooth ) this.smoothScrollContainerTo( maxScroll );
    else {
      container.scrollTop = Math.round( maxScroll );
      this._lastAim = maxScroll;
    }
  }

  scrollTOCToStart( { smooth = true } = {} ) {
    if ( !this.isDesktop() ) return;
    const container = this.toc;
    const curScroll = container.scrollTop;
    if ( curScroll <= this.cfg.epsilon ) return; // already at top
    this.edgeLock = 'top';
    this.setActiveLink( 0 ); // NEW: ensure first link is active at top
    if ( smooth ) this.smoothScrollContainerTo( 0 );
    else {
      container.scrollTop = 0;
      this._lastAim = 0;
    }
  }

  /* ==================== Events ==================== */
  onResize() {
    if ( this.isDesktop() ) this.applyDesktopStickyAndInnerScroll();
    else this.clearMobileStyles();

    // Keep first active if we're at/near top after layout changes
    if ( this.isSectionNearTop() ) this.setActiveLink( 0 ); // NEW

    // After layout changes, keep active visible (no animation to avoid jump)
    this.ensureActiveVisible( { smooth: false } );

    // Edge sync with locks
    if ( this.isSectionNearTop() ) this.scrollTOCToStart( { smooth: false } );
    else if ( this.isSectionNearBottom() ) this.scrollTOCToEnd( { smooth: false } );
    else this.edgeLock = null; // unlock when away from edges
  }

  onScroll() {
    if ( !this.allowScrollUpdate || !this.headings.length ) return;

    // Edge priority (prevents tug-of-war)
    if ( this.isSectionNearTop() ) {
      this.setActiveLink( 0 ); // NEW: ensure first is active while pinned to top
      this.scrollTOCToStart( { smooth: true } );
      return;
    }
    if ( this.isSectionNearBottom() ) {
      this.setActiveLink( this.links.length - 1 ); // NEW: ensure last is active at bottom
      this.scrollTOCToEnd( { smooth: true } );
      return;
    }

    // Away from edges → unlock and allow centering
    this.edgeLock = null;

    // Active link + centering
    const activeIndex = this.computeActiveIndex();
    if ( activeIndex === -1 ) {
      this.setActiveLink( 0 );
      this.ensureActiveVisible( { smooth: false } );
    } else {
      this.setActiveLink( activeIndex );
      this.ensureActiveVisible( { smooth: true } );
    }
  }

  onLinkClick( e ) {
    e.preventDefault();
    const href = e.currentTarget.getAttribute( 'href' );
    const id = href ? href.slice( 1 ) : null;
    const target = id ? document.getElementById( id ) : null;
    if ( !target ) return;

    this.allowScrollUpdate = false;

    const offset = this.headerHeight() + this.cfg.defaultOffset;
    const targetY = target.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo( { top: targetY, behavior: 'smooth' } );

    setTimeout( () => {
      this.allowScrollUpdate = true;
      this.onScroll();
    }, this.cfg.scrollDuration );
  }
}

/* ==================== init ==================== */
initStickyTOC( {
  navSelector: '.e-div--table-of-content',
  headerSelector: 'header',
  headingSelector: 'h2, .e-hdn--eye-brow:has(+ :is(h2, .h2))',
  linkSelector: 'li a',
  largeScreenWidth: 992,
  defaultOffset: 62,
  triggerOffsetDesktop: 400,
  triggerOffsetMobile: 200,
  marginTop: 12,
  marginBottom: 12,
  innerScrollDuration: 280,
  scrollDuration: 500,
  edgeThreshold: 6
} );
